import React, { useEffect, useState } from 'react';
import { CreditCard, Clock, X } from 'lucide-react';
import { Button } from './ui/button';

const PAYMENT_LINK = 'https://square.link/u/J2GrZj2T';

/**
 * Soft trial-ending reminder banner (Session 26 — private beta).
 *
 * Renders ONLY when:
 *   - Provider is on an active trial/promo (promoStatus === 'PROMO')
 *   - Provider does NOT have lifetime access
 *   - Provider is NOT a paid subscriber
 *   - We're currently inside a 10-day, 3-day, or 24-hour reminder window
 *
 * Returns null otherwise. Never blocks the dashboard.
 *
 * Props:
 *   provider: object with shape { promoStatus, promoEndDate, trialEndDate,
 *             subscriptionStatus, lifetimeAccess }
 */
export default function TrialReminderBanner({ provider }) {
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissal when the underlying window changes (e.g., moves from 10d → 3d)
  const reminder = computeReminder(provider);
  const reminderKey = reminder?.windowKey || null;

  useEffect(() => {
    if (!reminderKey) {
      setDismissed(false);
      return;
    }
    const storageKey = `rw_trial_reminder_dismissed_${reminderKey}`;
    try {
      setDismissed(localStorage.getItem(storageKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [reminderKey]);

  if (!reminder) return null;
  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(
        `rw_trial_reminder_dismissed_${reminder.windowKey}`,
        '1'
      );
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="trial-reminder-banner"
      data-window={reminder.windowKey}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="bg-amber-100 p-2 rounded-full shrink-0">
          <Clock className="h-5 w-5 text-amber-700" />
        </div>
        <div className="min-w-0">
          <p
            className="font-semibold text-amber-900 text-sm sm:text-base"
            data-testid="trial-reminder-headline"
          >
            {reminder.headline}
          </p>
          <p className="text-xs sm:text-sm text-amber-800 mt-0.5">
            Your RanchWorks trial ends soon. Add payment to keep your provider
            account active after your trial.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          asChild
          size="sm"
          className="bg-amber-700 hover:bg-amber-800 text-white"
          data-testid="trial-reminder-pay-btn"
        >
          <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer">
            <CreditCard className="h-4 w-4 mr-1" />
            Add Payment
          </a>
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="trial-reminder-dismiss"
          className="p-1 text-amber-700 hover:text-amber-900"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Pure computation: figure out which reminder window (if any) we're in.
 * Returns null if no reminder should show.
 */
function computeReminder(provider) {
  if (!provider) return null;
  if (provider.lifetimeAccess) return null;
  const sub = (provider.subscriptionStatus || '').toUpperCase();
  if (sub === 'PAID' || sub === 'ACTIVE' || sub === 'SUBSCRIBED') return null;
  if (provider.promoStatus !== 'PROMO') return null;

  const endIso = provider.promoEndDate || provider.trialEndDate;
  if (!endIso) return null;
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return null;

  const msLeft = end.getTime() - Date.now();
  if (msLeft <= 0) return null;

  const hoursLeft = msLeft / (1000 * 60 * 60);
  const daysLeft = hoursLeft / 24;

  let windowKey = null;
  let headline = '';
  if (hoursLeft <= 24) {
    windowKey = '24h';
    headline = 'Your trial ends in less than 24 hours';
  } else if (daysLeft <= 3) {
    windowKey = '3d';
    headline = `Your trial ends in ${Math.ceil(daysLeft)} days`;
  } else if (daysLeft <= 10) {
    windowKey = '10d';
    headline = `Your trial ends in ${Math.ceil(daysLeft)} days`;
  } else {
    return null;
  }

  // Window key bound to the trial end date so renewals get fresh reminders
  return {
    windowKey: `${windowKey}_${endIso.slice(0, 10)}`,
    headline,
  };
}
